import { Box, Container, Flex } from '@chakra-ui/react';
import { deleteCookie } from 'cookies-next';
import { GetServerSidePropsContext, NextPage } from 'next';
import { getSession } from 'next-auth/react';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { withCommonServerSideProps } from '@/lib/withCommonServerSideProps';

import { H1 } from '@/components/Heading';
import { LoginDisabled } from '@/components/Login/LoginDisabled';
import { LoginForm } from '@/components/Login/LoginForm';
import { LoginPowerPanel, OvilineTeaser } from '@/components/Login/LoginPowerPanel';
import Seo from '@/components/Seo';

import { CookieKeys } from '@/constants/cookie-keys';
import RoutePath from '@/constants/route-path';
import { Locks } from '@/models/api/Locks';
import { AppService } from '@/services';

type LoginPageProps = {
    locks: Locks;
};

type LoginPage = NextPage<LoginPageProps> & { hideHeader?: boolean };

const Login: LoginPage = (props) => {
    const intl = useIntl();
    const { loginAllowed, scheduleCallUrl } = props.locks;

    return (
        <>
            <Seo
                templateTitle={intl.formatMessage({ id: 'page.login.title' })}
                hideBreadCrumbs
            />
            {loginAllowed ? (
                <>
                    <Box display={{ base: 'none', lg: 'block' }} h="full">
                        <LoginPowerPanel scheduleCallUrl={scheduleCallUrl} />
                    </Box>
                    <Box display={{ base: 'block', lg: 'none' }}>
                        <Box
                            bg="#0d0d0c"
                            bgImage={`url(${AppService.getPathWithCountryCode(
                                '/images/login-power-panel-bg.webp'
                            )})`}
                            bgSize="100% auto"
                            bgPosition="center top"
                            bgRepeat="no-repeat"
                        >
                            <Container maxW="3xl" py={4} h="630px">
                                <Flex direction="column" justify="flex-end" h="full">
                                    <H1
                                        textAlign="center"
                                        color="white"
                                        fontSize={{
                                            base: '28px',
                                            lg: '38px'
                                        }}
                                    >
                                        <FormattedMessage id="page.login.title"></FormattedMessage>
                                    </H1>
                                    <LoginForm darkBackground variant="power-panel" />
                                </Flex>
                            </Container>
                        </Box>
                        <OvilineTeaser scheduleCallUrl={scheduleCallUrl} />
                    </Box>
                </>
            ) : (
                <LoginDisabled />
            )}
        </>
    );
};

Login.hideHeader = true;

export const getServerSideProps = withCommonServerSideProps(
    async (context: GetServerSidePropsContext) => {
        const { req, res } = context;
        deleteCookie(CookieKeys.showLoginDisabledPopup, { req, res });
        const session = await getSession(context);
        if (session) {
            return {
                redirect: {
                    destination: RoutePath.Profile,
                    permanent: false
                }
            };
        }
        return {
            props: {}
        };
    },
    { requiresAuth: false }
);

export default Login;
